import { t } from "@lingui/macro";
import { CalendarBlank, CaretDown, CaretRight, CaretUp, Check, Envelope, File, Globe, PencilSimple, Plus, Sparkle, Target, Translate, TrendUp, X } from "@phosphor-icons/react";
import { TOKEN_COSTS } from "@stylingcv/config";
import { defaultResumeData } from "@stylingcv/schema";
import { Button, Input } from "@stylingcv/ui";
import { cn } from "@stylingcv/utils";
import { formatDistance } from "date-fns";
import { useEffect, useState } from "react";

import { LocaleComboboxPopover } from "@/client/components/locale-combobox";
import { useTokenModal } from "@/client/contexts/token-modal-context";
import { useAdvancedResumeAnalysis } from "@/client/hooks/smart-ai/use-advanced-resume-analysis";
import { useCentralizedTokens } from "@/client/hooks/use-centralized-tokens";
import { useToast } from "@/client/hooks/use-toast";
import { useBuilderStore } from "@/client/stores/builder";
import { useResumeStore } from "@/client/stores/resume";
import { TranslateResumeModal } from "@/client/pages/builder/_components/translate-resume-modal";

const getFlagCode = (locale: string): string => {
  // Map specific locales to correct flag codes
  const flagMap: Record<string, string> = {
    "en-US": "us", // American flag for US English
    "ar-SA": "sa", // Saudi flag for Arabic
    "fr-FR": "fr", // French flag
    "es-ES": "es", // Spanish flag
    "de-DE": "de", // German flag
    "it-IT": "it", // Italian flag
    "pt-PT": "pt", // Portuguese flag
    "nl-NL": "nl", // Dutch flag
    "ru-RU": "ru", // Russian flag
    "zh-CN": "cn", // Chinese flag
    "ja-JP": "jp", // Japanese flag
    "ko-KR": "kr", // Korean flag
  };

  // First check the mapping, then fallback to extracting country code
  if (flagMap[locale]) {
    return flagMap[locale];
  }

  const countryCode = locale.split("-")[1]?.toLowerCase();
  return countryCode || "";
};

// Helper function to get language display name from locale
const getLanguageDisplayName = (locale: string): string => {
  const languageNames: Record<string, string> = {
    "en-US": "English",
    "ar-SA": "العربية",
    "fr-FR": "Français",
    "es-ES": "Español",
    "de-DE": "Deutsch",
    "it-IT": "Italiano",
    "pt-PT": "Português",
    "nl-NL": "Nederlands",
    "ru-RU": "Русский",
    "zh-CN": "中文",
    "ja-JP": "日本語",
    "ko-KR": "한국어",
  };

  return languageNames[locale] || locale;
};

export const ResumeInfoSection = () => {
  const { toast } = useToast();
  const [formattedDate, setFormattedDate] = useState<string>("");
  const [showLanguageEditor, setShowLanguageEditor] = useState(false);
  const [showTranslateModal, setShowTranslateModal] = useState(false);

  const resume = useResumeStore((state) => state.resume);
  const setValue = useResumeStore((state) => state.setValue);
  const metadata = useResumeStore((state) => {
    try {
      return state.resume.data.metadata;
    } catch {
      return defaultResumeData.metadata;
    }
  });

  // Get the setLeftPanelMode function from builder store
  const setLeftPanelMode = useBuilderStore((state) => state.setLeftPanelMode);
  const activeTab = useBuilderStore((state) => state.activeTab);
  const setActiveTab = useBuilderStore((state) => state.setActiveTab);
  const activeDocument = useBuilderStore((state) => state.activeDocument);
  const setActiveDocument = useBuilderStore((state) => state.setActiveDocument);
  const setShowCoverLetterModal = useBuilderStore((state) => state.setShowCoverLetterModal);

  // Token balance and modal
  const { tokenBalance } = useCentralizedTokens();
  const { showTokenModal } = useTokenModal();

  // Check if cover letter exists
  const hasCoverLetter = useResumeStore((state) => {
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const coverLetter = (state.resume.data as any).coverLetter;
      return !!(coverLetter?.body && coverLetter.body.trim().length > 0);
    } catch {
      return false;
    }
  });

  // Format the updated date
  useEffect(() => {
    try {
      const date = new Date(resume.updatedAt);
      const formatted = formatDistance(date, new Date(), { addSuffix: true });
      setFormattedDate(formatted);
    } catch {
      setFormattedDate("");
    }
  }, [resume.updatedAt]);

  const flagCode = getFlagCode(metadata.locale);

  // Capitalize template name for display
  const templateName = metadata.template.charAt(0).toUpperCase() + metadata.template.slice(1);

  // Function to handle template change - just switch to design panel
  const handleChangeTemplate = () => {
    // Simply set the left panel mode to 'design' to show the design panel
    setLeftPanelMode("design");
  };

  // Function to handle language change
  const handleLanguageChange = (newLocale: string) => {
    setValue("metadata.locale", newLocale);
    setShowLanguageEditor(false);
  };

  // Function to toggle language editor
  const handleChangeLanguage = () => {
    setShowLanguageEditor(!showLanguageEditor);
  };

  // Get analysis data
  const { getAnalysisSummary } = useAdvancedResumeAnalysis();
  const analysisSummary = getAnalysisSummary();

  // Helper for score color
  const getScoreColor = (score: number) => {
    if (score >= 80) return "text-green-600 dark:text-green-400";
    if (score >= 60) return "text-yellow-600 dark:text-yellow-400";
    return "text-red-600 dark:text-red-400";
  };

  // Title Editing State
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [titleValue, setTitleValue] = useState(resume.title || "");

  // Update title value when resume changes (external update)
  useEffect(() => {
    if (!isEditingTitle) {
      setTitleValue(resume.title || "");
    }
  }, [resume.title, isEditingTitle]);

  const handleUpdateTitle = () => {
    setValue("title", titleValue);
    setIsEditingTitle(false);
    toast({
      title: t`Title Updated`,
      description: t`Resume title has been updated successfully.`,
    });
  };

  return (
    <section id="resume-info" className="@container mb-6 flex flex-col gap-6 rounded-xl border border-white/5 bg-[#1f3647]/50 p-3 shadow-[inset_0_1px_1px_rgba(255,255,255,0.05),0_2px_8px_rgba(0,0,0,0.2)] backdrop-blur-sm sm:p-5">
      {/* Title Row / Header */}
      <div className="flex min-w-0 flex-col gap-1.5">
        <div className="min-w-0">
          {isEditingTitle ? (
            <div className="flex items-center gap-1.5">
              <Input
                autoFocus
                aria-label={t`Resume title`}
                value={titleValue}
                onChange={(e) => setTitleValue(e.target.value)}
                className="h-8 min-w-0 flex-1 border-white/10 bg-white/5 font-semibold text-white focus-visible:ring-brand-teal"
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleUpdateTitle();
                  if (e.key === "Escape") {
                    setIsEditingTitle(false);
                    setTitleValue(resume.title || "");
                  }
                }}
              />
              <Button
                size="icon"
                variant="ghost"
                aria-label={t`Save title`}
                className="size-8 shrink-0 text-brand-teal hover:bg-brand-teal/20 hover:text-brand-teal/80"
                onClick={handleUpdateTitle}
              >
                <Check size={16} aria-hidden="true" />
              </Button>
              <Button
                size="icon"
                variant="ghost"
                aria-label={t`Cancel editing`}
                className="size-8 shrink-0 text-white/50 hover:bg-white/10 hover:text-white"
                onClick={() => {
                  setIsEditingTitle(false);
                  setTitleValue(resume.title || "");
                }}
              >
                <X size={16} aria-hidden="true" />
              </Button>
            </div>
          ) : (
            <div className="group flex min-w-0 items-center gap-2">
              <Button
                size="icon"
                variant="ghost"
                aria-label={t`Edit title`}
                className="size-6 shrink-0 text-white/40 opacity-0 transition-opacity group-hover:opacity-100 hover:bg-white/10 hover:text-white"
                onClick={(e) => {
                  e.stopPropagation();
                  setIsEditingTitle(true);
                }}
              >
                <PencilSimple size={14} aria-hidden="true" />
              </Button>
              <h3
                className="min-w-0 truncate text-base font-bold text-white transition-colors cursor-pointer sm:text-lg hover:text-brand-teal"
                title={resume.title || t`Untitled Resume`}
                onClick={() => setIsEditingTitle(true)}
              >
                {resume.title || t`Untitled Resume`}
              </h3>
            </div>
          )}
        </div>

        {formattedDate && (
          <div className="flex items-center gap-1.5 text-[11px] text-white/40">
            <CalendarBlank size={12} />
            <span>{t`Modified`}: {formattedDate}</span>
          </div>
        )}
      </div>

      <div className="flex flex-col gap-4 animate-in fade-in slide-in-from-top-2 duration-200">
        <div className="grid grid-cols-1 gap-2 @[340px]:grid-cols-2 sm:gap-3">
          
          {/* 1. Language Tile */}
          {showLanguageEditor ? (
             <div className="col-span-1 flex min-h-[5rem] flex-col items-center justify-center rounded-xl border border-white/10 bg-white/5 p-4 backdrop-blur-sm @[340px]:col-span-2">
                <LocaleComboboxPopover
                  value={metadata.locale}
                  onValueChange={handleLanguageChange}
                />
                <Button
                  size="sm"
                  variant="ghost"
                  className="mt-2 h-8 text-xs font-semibold text-white/70 hover:text-white"
                  onClick={() => setShowLanguageEditor(false)}
                >
                  {t`Cancel`}
                </Button>
             </div>
          ) : (
            <div 
              className={cn(
                "group relative flex cursor-pointer flex-col items-center rounded-[14px] border border-white/5 bg-[#1f3647]/50 p-2.5 sm:p-3 sm:flex-row sm:items-center sm:gap-3 transition-all",
                "hover:border-white/10 hover:bg-[#254256]/60 shadow-[inset_0_1px_1px_rgba(255,255,255,0.05),0_2px_8px_rgba(0,0,0,0.2)]",
                activeTab === "fill-in" && "border-brand-teal/30 bg-[#254256]/80 shadow-[inset_0_1px_1px_rgba(255,255,255,0.1),0_0_12px_rgba(38,133,153,0.15)]"
              )}
              onClick={() => {
                if (tokenBalance < TOKEN_COSTS.LOCALIZATION) {
                  showTokenModal({
                    requiredTokens: TOKEN_COSTS.LOCALIZATION,
                    currentTokens: tokenBalance,
                    featureName: "Resume Translation",
                  });
                  return;
                }
                setShowTranslateModal(true);
              }}
            >
              <div
                className="absolute top-1 right-1.5 sm:top-1.5 sm:right-2 flex items-center gap-0.5 rounded-full bg-gradient-to-r from-violet-500/20 to-fuchsia-500/20 px-1.5 py-0.5 ring-1 ring-violet-400/20"
                title={t`AI-powered · ${TOKEN_COSTS.LOCALIZATION} tokens`}
              >
                <Sparkle size={10} weight="fill" className="text-violet-400" />
              </div>
              <div className="flex h-9 w-9 sm:h-11 sm:w-11 shrink-0 items-center justify-center rounded-[10px] bg-white/5 text-brand-teal/70 shadow-inner ring-1 ring-white/10">
                {flagCode ? (
                  <span
                    className={`fi fi-${flagCode} rounded-[2px] shadow-sm scale-110`}
                    style={{ width: "16px", height: "12px" }}
                  />
                ) : (
                  <Translate size={18} weight="regular" />
                )}
              </div>
              <div className="mt-1.5 sm:mt-0 flex min-w-0 flex-1 flex-col items-center sm:items-start overflow-hidden">
                <span className="text-[10px] font-semibold tracking-wider text-white/50 uppercase">{t`Language`}</span>
                <button
                  className="mt-0.5 flex shrink-0 items-center gap-0.5 text-[10px] font-medium text-[oklch(68.5%_0.169_237.323)] transition-colors hover:brightness-125"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (tokenBalance < TOKEN_COSTS.LOCALIZATION) {
                      showTokenModal({
                        requiredTokens: TOKEN_COSTS.LOCALIZATION,
                        currentTokens: tokenBalance,
                        featureName: "Resume Translation",
                      });
                      return;
                    }
                    setShowTranslateModal(true);
                  }}
                >
                  {t`Copy to other language`}
                  <CaretRight size={10} weight="bold" />
                </button>
              </div>
            </div>
          )}

          {/* 2. Template Tile */}
          <div 
            className={cn(
              "group relative flex cursor-pointer flex-col items-center rounded-[14px] border border-white/5 bg-[#1f3647]/50 p-2.5 sm:p-3 sm:flex-row sm:items-center sm:gap-3 transition-all",
              "hover:border-white/10 hover:bg-[#254256]/60 shadow-[inset_0_1px_1px_rgba(255,255,255,0.05),0_2px_8px_rgba(0,0,0,0.2)]",
              activeTab === "design" && "border-brand-teal/30 bg-[#254256]/80 shadow-[inset_0_1px_1px_rgba(255,255,255,0.1),0_0_12px_rgba(38,133,153,0.15)]"
            )}
            onClick={() => {
              setActiveTab("design");
              setLeftPanelMode("design");
            }}
          >
            <div className="flex h-9 w-9 sm:h-11 sm:w-11 shrink-0 items-center justify-center rounded-[10px] bg-white/5 text-white/80 shadow-inner ring-1 ring-white/10">
              <Target size={18} weight="regular" />
            </div>
            <div className="mt-1.5 sm:mt-0 flex min-w-0 flex-1 flex-col items-center sm:items-start overflow-hidden">
              <span className="text-[10px] font-semibold tracking-wider text-white/50 uppercase">{t`Design`}</span>
              <button
                className="mt-0.5 flex shrink-0 items-center gap-0.5 text-[10px] font-medium text-[oklch(68.5%_0.169_237.323)] transition-colors hover:brightness-125"
                onClick={(e) => {
                  e.stopPropagation();
                  setActiveTab("design");
                  setLeftPanelMode("design");
                }}
              >
                {t`Change template`}
                <CaretRight size={10} weight="bold" />
              </button>
            </div>
          </div>

          {/* 3. ATS Score Tile */}
          <div
            className={cn(
               "group relative flex cursor-pointer flex-col items-center rounded-[14px] border border-white/5 bg-[#1f3647]/50 p-2.5 sm:p-3 sm:flex-row sm:items-center sm:gap-3 transition-all",
               "hover:border-white/10 hover:bg-[#254256]/60 shadow-[inset_0_1px_1px_rgba(255,255,255,0.05),0_2px_8px_rgba(0,0,0,0.2)]",
              activeTab === "analysis" && "border-brand-teal/30 bg-[#254256]/80 shadow-[inset_0_1px_1px_rgba(255,255,255,0.1),0_0_12px_rgba(38,133,153,0.15)]"
            )}
            onClick={() => {
              setActiveTab("analysis");
              setLeftPanelMode("content");
              setTimeout(() => {
                const analysisSection = document.querySelector(".analysis-section-card");
                analysisSection?.scrollIntoView({ behavior: "smooth" });
              }, 100);
            }}
          >
            <div
              className="absolute top-1 right-1.5 sm:top-1.5 sm:right-2 flex items-center gap-0.5 rounded-full bg-gradient-to-r from-violet-500/20 to-fuchsia-500/20 px-1.5 py-0.5 ring-1 ring-violet-400/20"
              title={t`AI-powered · ${TOKEN_COSTS.ADVANCED_RESUME_ANALYSIS} tokens`}
            >
              <Sparkle size={10} weight="fill" className="text-violet-400" />
            </div>
            <div className="flex h-9 w-9 sm:h-11 sm:w-11 shrink-0 items-center justify-center rounded-[10px] bg-white/5 text-brand-teal/70 shadow-inner ring-1 ring-white/10">
              <TrendUp size={18} weight="regular" />
            </div>
            <div className="mt-1.5 sm:mt-0 flex min-w-0 flex-1 flex-col items-center sm:items-start overflow-hidden">
              <span className="text-[10px] font-semibold tracking-wider text-white/50 uppercase">{t`ATS`}</span>
              <button
                className="mt-0.5 flex shrink-0 items-center gap-0.5 text-[10px] font-medium text-[oklch(68.5%_0.169_237.323)] transition-colors hover:brightness-125"
                onClick={(e) => {
                  e.stopPropagation();
                  setActiveTab("analysis");
                  setLeftPanelMode("content");
                  setTimeout(() => {
                    document.querySelector(".analysis-section-card")?.scrollIntoView({ behavior: "smooth" });
                  }, 100);
                }}
              >
                {analysisSummary?.hasData ? t`Check again` : t`Check now`}
                <CaretRight size={10} weight="bold" />
              </button>
            </div>
          </div>

          {/* 4. Cover Letter Tile */}
          <div 
            className={cn(
               "group relative flex cursor-pointer flex-col items-center rounded-[14px] border border-brand-coral/20 bg-[#1f3647]/50 p-2.5 sm:p-3 sm:flex-row sm:items-center sm:gap-3 transition-all",
               "hover:border-brand-coral/40 hover:bg-[#254256]/60 shadow-[inset_0_1px_1px_rgba(255,255,255,0.05),0_2px_8px_rgba(0,0,0,0.2)]",
              activeDocument === "cover-letter" && "border-brand-coral/50 bg-[#254256]/80 shadow-[inset_0_1px_1px_rgba(255,255,255,0.1),0_0_12px_rgba(232,136,94,0.15)]"
            )}
            onClick={() => {
              if (activeDocument === "cover-letter") {
                 setActiveDocument("resume");
                 setActiveTab("fill-in");
                 setLeftPanelMode("content");
              } else if (hasCoverLetter) {
                 setActiveDocument("cover-letter");
                 setActiveTab("fill-in");
                 setLeftPanelMode("content");
              } else {
                 if (tokenBalance < TOKEN_COSTS.COVER_LETTER) {
                   showTokenModal({
                     requiredTokens: TOKEN_COSTS.COVER_LETTER,
                     currentTokens: tokenBalance,
                     featureName: "Cover Letter Generation",
                   });
                   return;
                 }
                 setShowCoverLetterModal(true);
              }
            }}
          >
            <div
              className="absolute top-1 right-1.5 sm:top-1.5 sm:right-2 flex items-center gap-0.5 rounded-full bg-gradient-to-r from-violet-500/20 to-fuchsia-500/20 px-1.5 py-0.5 ring-1 ring-violet-400/20"
              title={t`AI-powered · ${TOKEN_COSTS.COVER_LETTER} tokens`}
            >
              <Sparkle size={10} weight="fill" className="text-violet-400" />
            </div>
            <div className="flex h-9 w-9 sm:h-11 sm:w-11 shrink-0 items-center justify-center rounded-[10px] bg-white/5 text-brand-coral/70 shadow-inner ring-1 ring-brand-coral/20">
              <File size={18} weight="regular" />
            </div>
            <div className="mt-1.5 sm:mt-0 flex min-w-0 flex-1 flex-col items-center sm:items-start overflow-hidden">
              <span className="text-[10px] font-semibold tracking-wider text-white/50 uppercase">
                {activeDocument === "cover-letter" ? t`Resume` : t`Cover Letter`}
              </span>
              <button
                className="mt-0.5 flex shrink-0 items-center gap-0.5 text-[10px] font-medium text-[oklch(68.5%_0.169_237.323)] transition-colors hover:brightness-125"
                onClick={(e) => {
                  e.stopPropagation();
                  if (activeDocument === "cover-letter") {
                    setActiveDocument("resume");
                    setActiveTab("fill-in");
                    setLeftPanelMode("content");
                  } else if (hasCoverLetter) {
                    setActiveDocument("cover-letter");
                    setActiveTab("fill-in");
                    setLeftPanelMode("content");
                  } else {
                    if (tokenBalance < TOKEN_COSTS.COVER_LETTER) {
                      showTokenModal({
                        requiredTokens: TOKEN_COSTS.COVER_LETTER,
                        currentTokens: tokenBalance,
                        featureName: "Cover Letter Generation",
                      });
                      return;
                    }
                    setShowCoverLetterModal(true);
                  }
                }}
              >
                {activeDocument === "cover-letter" ? t`Back` : hasCoverLetter ? t`Show` : t`Create with AI`}
                <CaretRight size={10} weight="bold" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Translate Resume Modal */}
      <TranslateResumeModal
        isOpen={showTranslateModal}
        onClose={() => setShowTranslateModal(false)}
      />
    </section>
  );
};